import { createWithEqualityFn } from 'zustand/traditional'
import axios from 'axios'
import { cleanParamsFilter } from 'Utils/cleanParamsFilter'

import { CRUDBaseStore } from './Base/CRUD'

interface ProjectStore extends CRUDBaseStore {
  piles: any[]
  pileHoles: any[]
  pileHole: any
  weldPictures: any[]
  machines: any[]
  availableSiteSupervisors: any[]
  getOne: (id: number) => Promise<{ success: boolean; data: any }>
  getPiles: (id: number) => Promise<{ success: boolean; data: any }>
  getAvailableSiteSupervisors: () => Promise<{ success: boolean; data: any }>
  setPiles: (data: any) => void
  setPileHoles: (data: any) => void
  setPileHole: (data: any) => void
  scanReceivePile: (id: number, data: any) => Promise<{ success: boolean; data: any }>
  pileDriving: (
    projectId: number,
    data: {
      inventoryCode: string
      pileHoleId?: number | undefined | null
      projectMachineId?: number | undefined | null
      pileNo?: string | undefined | null
      gridLine?: string | undefined | null
      location?: string | undefined | null
    }
  ) => Promise<{ success: boolean; data: any }>
  getPileHoles: (projectId: number) => Promise<{ success: boolean; data: any }>
  getPileHoleInfo: (
    projectId: number,
    pileHoldeId: number
  ) => Promise<{ success: boolean; data: any }>
  removePileDriving: (
    projectId: number,
    data: { inventoryCode: string; pileHoleId: number }
  ) => void
  open: (projectId: number) => Promise<{ success: boolean; data: any }>
  close: (projectId: number) => Promise<{ success: boolean; data: any }>
  closeHole: (
    projectId: number,
    data: { pileHoleId: number }
  ) => Promise<{ success: boolean; data: any }>
  changePileHoleStatus: (
    projectId: string,
    pileHoleId: string,
    status: string
  ) => Promise<{ success: boolean; data: any }>
  // Welds Picture
  getAllWeldPictures: (
    projectId: string,
    pileHoleId: string
  ) => Promise<{ success: boolean; data: any }>
  viewWeldPicture: (path: string) => string
  createWeldPicture: (
    projectId: string,
    pileHoleId: string,
    data: any
  ) => Promise<{ success: boolean; data: any }>
  deleteWeldPicture: (
    projectId: string,
    pileHoleId: string,
    id: string
  ) => Promise<{ success: boolean; data: any }>
  // Welds Picture
  downloadAllDailyReport: () => Promise<{ success: boolean; data: any }>
  downloadDailyReportByProjetId: (projectId: string) => Promise<{ success: boolean; data: any }>
  getAllProjectMachine: (projectId: string) => Promise<{ success: boolean; data: any }>
  assignMachineToProject: (projectId: string, data: any) => Promise<{ success: boolean; data: any }>
  updateMachineToProject: (
    projectId: string,
    machineId: string,
    data: any
  ) => Promise<{ success: boolean; data: any }>
  deleteMachineToProject: (
    projectId: string,
    itemId: string
  ) => Promise<{ success: boolean; data: any }>
  assignSiteSupervisor: (projectId: number, data: any) => Promise<{ success: boolean; data: any }>
  unassignSiteSupervisor: (projectId: number, data: any) => Promise<{ success: boolean; data: any }>
  addNote: (projectId: number, data: any) => Promise<{ success: boolean; data: any }>
  removeNote: (projectId: number, noteId: number) => Promise<{ success: boolean; data: any }>
  downloadFileNote: (
    projectId: number,
    noteId: number,
    fileId: string,
    documentName: string
  ) => Promise<{ success: boolean; data: any }>
  clearState: () => void
}

export const API_SLUG = 'project'

export const INITIAL_STATE = {}

export const useProjectStore = createWithEqualityFn<ProjectStore>((set, get) => ({
  data: [],
  piles: [],
  pileHoles: [],
  availableSiteSupervisors: [],
  pileHole: null,
  weldPictures: [],
  machines: [],
  selected: INITIAL_STATE,
  loading: false,
  error: undefined,
  setLoading: (loading: boolean) => {
    set({ loading })
  },
  setError: (error: any) => {
    set({ error })
  },
  setSelected: (selected: any) => {
    set({ selected })
  },
  setPiles: (data: any) => {
    set({ piles: data })
  },
  setPileHoles: (data: any) => {
    set({ pileHoles: data })
  },
  setPileHole: (data: any) => {
    set({ pileHole: data })
  },
  getAll: async (filter: any) => {
    set({ loading: true })
    const finaleFilter = cleanParamsFilter(filter)

    return axios
      .get(`/${API_SLUG}`, { params: finaleFilter })
      .then(res => {
        set({ data: res.data })
        set({ loading: false })

        return { success: true, data: res.data }
      })
      .catch(err => {
        set({ loading: false })
        set({ error: err?.response?.data?.message?.toString() })
        return { success: false, data: err?.response?.data?.message?.toString() }
      })
  },
  getOne: async id => {
    set({ loading: true })

    return axios
      .get(`/${API_SLUG}/${id}`)
      .then(res => {
        set({ selected: res.data })
        set({ loading: false })

        return { success: true, data: res.data }
      })
      .catch(err => {
        set({ loading: false })
        set({ error: err?.response?.data?.message?.toString() })
        return { success: false, data: err?.response?.data?.message?.toString() }
      })
  },
  getAvailableSiteSupervisors: async () => {
    set({ loading: true })

    return axios
      .get(`/${API_SLUG}/available-site-supervisors`)
      .then(res => {
        set({ availableSiteSupervisors: res.data })
        set({ loading: false })

        return { success: true, data: res.data }
      })
      .catch(err => {
        set({ loading: false })
        set({ error: err?.response?.data?.message?.toString() })
        return { success: false, data: err?.response?.data?.message?.toString() }
      })
  },
  getPiles: async id => {
    set({ loading: true })

    return axios
      .get(`/${API_SLUG}/${id}/piles`)
      .then(res => {
        set({ piles: res.data })
        set({ loading: false })

        return { success: true, data: res.data }
      })
      .catch(err => {
        set({ loading: false })
        set({ error: err?.response?.data?.message?.toString() })
        return { success: false, data: err?.response?.data?.message?.toString() }
      })
  },
  getPileHoles: async projectId => {
    set({ loading: true })

    return axios
      .get(`/${API_SLUG}/${projectId}/pile-hole`)
      .then(res => {
        set({ pileHoles: res.data })
        set({ loading: false })

        return { success: true, data: res.data }
      })
      .catch(err => {
        set({ pileHoles: [] })
        set({ loading: false })
        set({ error: err?.response?.data?.message?.toString() })
        return { success: false, data: err?.response?.data?.message?.toString(), err }
      })
  },
  getPileHoleInfo: async (projectId: number, pileHoleId: number) => {
    set({ loading: true })

    return axios
      .get(`/${API_SLUG}/${projectId}/pile-hole/${pileHoleId}`)
      .then(res => {
        set({ pileHole: res.data })
        set({ loading: false })

        return { success: true, data: res.data }
      })
      .catch(err => {
        set({ loading: false })
        set({ error: err?.response?.data?.message?.toString() })
        return { success: false, data: err?.response?.data?.message?.toString() }
      })
  },
  create: async (data: any) => {
    set({ loading: true })

    const formData = new FormData()
    Object.keys(data).forEach(key => {
      if (data[key] !== undefined) {
        formData.append(key, data[key])
      }
    })

    return axios
      .post(`/${API_SLUG}`, formData)
      .then(res => {
        set({ loading: false })
        return { success: true, data: res.data }
      })
      .catch(err => {
        set({ loading: false })
        set({ error: err?.response?.data?.message?.toString() })
        return { success: false, data: err?.response?.data?.message?.toString() }
      })
  },
  edit: async (id, data) => {
    set({ loading: true })

    const formData = new FormData()
    Object.keys(data).forEach(key => {
      if (data[key] !== undefined) {
        formData.append(key, data[key])
      }
    })

    return axios
      .put(`/${API_SLUG}/${id}`, formData)
      .then(res => {
        set({ loading: false })
        return { success: true, data: res.data }
      })
      .catch(err => {
        set({ loading: false })
        set({ error: err?.response?.data?.message?.toString() })
        return { success: false, data: err?.response?.data?.message?.toString() }
      })
  },
  delete: async id => {
    set({ loading: true })
    return axios
      .delete(`/${API_SLUG}/${id}`)
      .then(res => {
        set({ loading: false })
        return { success: true, data: res.data }
      })
      .catch(err => {
        set({ loading: false })
        set({ error: err?.response?.data?.message?.toString() })
        return { success: false, data: err?.response?.data?.message?.toString() }
      })
  },
  open: async id => {
    set({ loading: true })
    return axios
      .post(`/${API_SLUG}/${id}/open`)
      .then(res => {
        set({ loading: false })
        return { success: true, data: null }
      })
      .catch(err => {
        set({ loading: false })
        set({ error: err?.response?.data?.message?.toString() })
        return { success: false, data: err?.response?.data?.message?.toString() }
      })
  },
  close: async id => {
    set({ loading: true })
    return axios
      .post(`/${API_SLUG}/${id}/close`)
      .then(res => {
        set({ loading: false })
        return { success: true, data: null }
      })
      .catch(err => {
        set({ loading: false })
        set({ error: err?.response?.data?.message?.toString() })
        return { success: false, data: err?.response?.data?.message?.toString() }
      })
  },
  scanReceivePile: async (id, data) => {
    set({ loading: true })
    const formData = new FormData()
    Object.keys(data).forEach(key => {
      if (data[key] !== undefined) {
        formData.append(key, data[key])
      }
    })

    return axios
      .post(`/${API_SLUG}/${id}/scan-receive`, formData)
      .then(res => {
        set({ loading: false })
        return { success: true, data: res.data }
      })
      .catch(err => {
        set({ loading: false })
        set({ error: err?.response?.data?.message?.toString() })
        return { success: false, data: err?.response?.data?.message?.toString() }
      })
  },
  pileDriving: async (
    projectId: number,
    data: {
      inventoryCode: string
      pileHoleId?: number | undefined | null
      projectMachineId?: number | undefined | null
      pileNo?: string | undefined | null
      gridLine?: string | undefined | null
      location?: string | undefined | null
    }
  ) => {
    set({ loading: true })
    const formData = new FormData()

    formData.append('inventoryCode', data.inventoryCode)

    if (data.pileHoleId) formData.append('pileHoleId', data.pileHoleId.toString())
    if (data.projectMachineId) formData.append('projectMachineId', data.projectMachineId.toString())
    if (data.pileNo) formData.append('pileNo', data.pileNo)
    if (data.gridLine) formData.append('gridLine', data.gridLine)
    if (data.location) formData.append('location', data.location)

    return axios
      .post(`/${API_SLUG}/${projectId}/pile-driving`, formData)
      .then(res => {
        set({ loading: false })
        return { success: true, data: res.data }
      })
      .catch(err => {
        set({ loading: false })
        set({ error: err?.response?.data?.message?.toString() })
        return { success: false, data: err?.response?.data?.message?.toString() }
      })
  },
  removePileDriving: async (
    projectId: number,
    data: { inventoryCode: string; pileHoleId: number }
  ) => {
    set({ loading: true })
    const formData = new FormData()

    formData.append('inventoryCode', data.inventoryCode)
    formData.append('pileHoleId', data.pileHoleId.toString())

    return axios
      .delete(`/${API_SLUG}/${projectId}/pile-driving`, { data: formData })
      .then(res => {
        set({ loading: false })
        return true
      })
      .catch(err => {
        set({ loading: false })
        set({ error: err?.response?.data?.message?.toString() })
        return false
      })
  },
  closeHole: async (projectId: number, data: { pileHoleId: number }) => {
    set({ loading: true })
    const formData = new FormData()
    formData.append('pileHoleId', data.pileHoleId.toString())

    return axios
      .post(`/${API_SLUG}/${projectId}/close-hole`, formData)
      .then(res => {
        set({ loading: false })
        return { success: true, data: res.data }
      })
      .catch(err => {
        set({ loading: false })
        set({ error: err?.response?.data?.message?.toString() })
        return { success: false, data: err?.response?.data?.message?.toString() }
      })
  },
  changePileHoleStatus: async (projectId: string, pileHoleId: string, status: string) => {
    const formData = new FormData()
    formData.append('status', status)

    return axios
      .patch(`/${API_SLUG}/${projectId}/pile-hole/${pileHoleId}/status`, formData)
      .then(res => {
        return { success: true, data: res.data }
      })
      .catch(err => {
        return { success: false, data: err?.response?.data?.message?.toString() }
      })
  },
  getAllWeldPictures: async (projectId: string, pileHoleId: string) => {
    return axios
      .get(`/${API_SLUG}/${projectId}/piles/${pileHoleId}/attachWeldsPicture`)
      .then(res => {
        set({ weldPictures: res.data })
        return { success: true, data: res.data }
      })
      .catch(err => {
        return { success: false, data: err?.response?.data?.message?.toString() }
      })
  },
  viewWeldPicture: (path: string) => {
    return `${process.env.REACT_APP_BACKEND_API_URL}${path}`
  },
  createWeldPicture: async (projectId: string, pileHoleId: string, data: any) => {
    const formData = new FormData()
    Object.keys(data).forEach(key => {
      if (data[key] !== undefined) {
        formData.append(key, data[key])
      }
    })

    return axios
      .post(`/${API_SLUG}/${projectId}/piles/${pileHoleId}/attachWeldsPicture`, formData)
      .then(res => {
        return { success: true, data: res.data }
      })
      .catch(err => {
        return { success: false, data: err?.response?.data?.message?.toString() }
      })
  },
  deleteWeldPicture: async (projectId: string, pileHoleId: string, id: string) => {
    return axios
      .delete(`/${API_SLUG}/${projectId}/piles/${pileHoleId}/attachWeldsPicture/${id}`)
      .then(res => {
        return { success: true, data: res.data }
      })
      .catch(err => {
        return { success: false, data: err?.response?.data?.message?.toString() }
      })
  },
  downloadAllDailyReport: async () => {
    return axios
      .get(`/${API_SLUG}/dailyReport`, { responseType: 'blob' })
      .then(res => {
        const url = window.URL.createObjectURL(new Blob([res.data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', 'daily-report.xlsx')
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)

        return { success: true, data: res.data }
      })
      .catch(err => {
        return { success: false, data: err?.response?.data?.message?.toString() }
      })
  },
  downloadDailyReportByProjetId: async (projectId: string) => {
    return axios
      .get(`/${API_SLUG}/dailyReport?projectId=${projectId}`, { responseType: 'blob' })
      .then(res => {
        const url = window.URL.createObjectURL(new Blob([res.data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', 'daily-report.xlsx')
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)

        return { success: true, data: res.data }
      })
      .catch(err => {
        return { success: false, data: err?.response?.data?.message?.toString() }
      })
  },
  getAllProjectMachine: async (projectId: string) => {
    return axios
      .get(`/${API_SLUG}/${projectId}/machines`)
      .then(res => {
        set({ machines: res.data })
        return { success: true, data: res.data }
      })
      .catch(err => {
        return { success: false, data: err?.response?.data?.message?.toString() }
      })
  },
  assignMachineToProject: async (projectId: string, data: any) => {
    const formData = new FormData()
    Object.keys(data).forEach(key => {
      if (data[key] !== undefined && data[key] !== null) {
        formData.append(key, data[key])
      }
    })

    return axios
      .post(`/${API_SLUG}/${projectId}/machines`, formData)
      .then(res => {
        return { success: true, data: res.data }
      })
      .catch(err => {
        return { success: false, data: err?.response?.data?.message?.toString() }
      })
  },
  updateMachineToProject: async (projectId: string, machineId: string, data: any) => {
    const formData = new FormData()
    Object.keys(data).forEach(key => {
      if (data[key] !== undefined && data[key] !== null) {
        formData.append(key, data[key])
      }
    })

    return axios
      .patch(`/${API_SLUG}/${projectId}/machines/${machineId}`, formData)
      .then(res => {
        return { success: true, data: res.data }
      })
      .catch(err => {
        return { success: false, data: err?.response?.data?.message?.toString() }
      })
  },
  deleteMachineToProject: async (projectId: string, machineId: string) => {
    return axios
      .delete(`/${API_SLUG}/${projectId}/machines/${machineId}`)
      .then(res => {
        return { success: true, data: res.data }
      })
      .catch(err => {
        return { success: false, data: err?.response?.data?.message?.toString() }
      })
  },
  assignSiteSupervisor: async (projectId: number, data: any) => {
    return axios
      .post(`/${API_SLUG}/${projectId}/assign-site-supervisor`, data)
      .then(res => {
        return { success: true, data: res.data }
      })
      .catch(err => {
        return { success: false, data: err?.response?.data?.message?.toString() }
      })
  },
  unassignSiteSupervisor: async (projectId: number, data: any) => {
    return axios
      .post(`/${API_SLUG}/${projectId}/remove-site-supervisor`, data)
      .then(res => {
        return { success: true, data: res.data }
      })
      .catch(err => {
        return { success: false, data: err?.response?.data?.message?.toString() }
      })
  },
  addNote: async (projectId: number, data: any) => {
    return axios
      .post(`/${API_SLUG}/${projectId}/add-note`, data)
      .then(res => {
        return { success: true, data: res.data }
      })
      .catch(err => {
        return { success: false, data: err?.response?.data?.message?.toString() }
      })
  },
  removeNote: async (projectId: number, noteId: number) => {
    return axios
      .delete(`/${API_SLUG}/${projectId}/delete-note/${noteId}`)
      .then(res => {
        return { success: true, data: res.data }
      })
      .catch(err => {
        return { success: false, data: err?.response?.data?.message?.toString() }
      })
  },
  downloadFileNote: async (
    projectId: number,
    noteId: number,
    fileId: string,
    documentName: string
  ) => {
    return axios
      .get(`/${API_SLUG}/${projectId}/notes/${noteId}/file/${fileId}`, { responseType: 'blob' })
      .then(res => {
        const url = window.URL.createObjectURL(new Blob([res.data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', documentName)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)

        return { success: true, data: res.data }
      })
      .catch(err => {
        return { success: false, data: err?.response?.data?.message?.toString() }
      })
  },
  clearState: () => {
    set({
      data: [],
      piles: [],
      pileHoles: [],
      weldPictures: [],
      pileHole: null,
      loading: false,
      error: undefined,
      selected: INITIAL_STATE,
    })
  },
}))
